// Local, server-free transfer of a user's whole index between the two builds
// of this app (browser extension ↔ same-origin web SPA). IndexedDB is
// partitioned by origin, so the extension's storage and the web app's storage
// are physically separate even on the same machine — there is no API that
// "shares" them. This module bridges them the only privacy-preserving way:
// the user exports a file from one build and imports it into the other. The
// data never touches a server; it only ever lives in a file the user controls.
//
// Auth material (oidc.* settings) is deliberately excluded — a bundle must be
// safe to hand to a colleague without leaking refresh tokens.

import { getDB } from "../db/schema";
import { tr } from "../i18n/i18n.svelte";
import type {
  Conversation,
  Message,
  SettingsEntry,
  StoredChunk,
  StoredDocument,
} from "../db/types";

const BUNDLE_FORMAT = "personal-rag-bundle";
const BUNDLE_VERSION = 1;

// Settings whose key starts with any of these prefixes are never exported.
const SETTINGS_DENYLIST = ["oidc."];

interface SerializedChunk {
  id: string;
  text: string;
  page: number;
  section: string | null;
  /** Float32Array embedding, base64-encoded (raw little-endian bytes). */
  embedding: string;
}

type SerializedDocument = Omit<StoredDocument, "chunks"> & {
  chunks: SerializedChunk[];
};

export interface PersonalRagBundle {
  format: typeof BUNDLE_FORMAT;
  version: number;
  exportedAt: number;
  source: "extension" | "web";
  documents: SerializedDocument[];
  conversations: Conversation[];
  messages: Message[];
  settings: SettingsEntry[];
}

export interface ExportResult {
  blob: Blob;
  filename: string;
  stats: { documents: number; conversations: number; messages: number };
}

export interface ImportSummary {
  documentsAdded: number;
  documentsSkipped: number;
  conversationsAdded: number;
  conversationsSkipped: number;
  messagesAdded: number;
  settingsApplied: number;
}

// ---- embedding (de)serialization ------------------------------------------

function embeddingToBase64(arr: Float32Array): string {
  const bytes = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function base64ToEmbedding(b64: string): Float32Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Float32Array(bytes.buffer);
}

function serializeChunk(c: StoredChunk): SerializedChunk {
  const emb =
    c.embedding instanceof Float32Array
      ? c.embedding
      : Float32Array.from(c.embedding as ArrayLike<number>);
  return {
    id: c.id,
    text: c.text,
    page: c.page,
    section: c.section,
    embedding: embeddingToBase64(emb),
  };
}

function deserializeChunk(c: SerializedChunk): StoredChunk {
  return {
    id: c.id,
    text: c.text,
    page: c.page,
    section: c.section,
    embedding: base64ToEmbedding(c.embedding),
  };
}

// ---- export ---------------------------------------------------------------

export async function buildBundle(): Promise<ExportResult> {
  const db = await getDB();
  const [docs, conversations, messages, settings] = await Promise.all([
    db.getAll("documents"),
    db.getAll("conversations"),
    db.getAll("messages"),
    db.getAll("settings"),
  ]);

  const bundle: PersonalRagBundle = {
    format: BUNDLE_FORMAT,
    version: BUNDLE_VERSION,
    exportedAt: Date.now(),
    source: __PLATFORM__ === "web" ? "web" : "extension",
    documents: docs.map((d) => ({ ...d, chunks: d.chunks.map(serializeChunk) })),
    conversations,
    messages,
    settings: settings.filter(
      (s) => !SETTINGS_DENYLIST.some((p) => s.key.startsWith(p)),
    ),
  };

  const blob = new Blob([JSON.stringify(bundle)], {
    type: "application/json",
  });
  return {
    blob,
    filename: `personal-rag-index-${dateStamp()}.json`,
    stats: {
      documents: docs.length,
      conversations: conversations.length,
      messages: messages.length,
    },
  };
}

export function triggerBundleDownload(result: ExportResult): void {
  const url = URL.createObjectURL(result.blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = result.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---- import ---------------------------------------------------------------

function isBundle(v: unknown): v is PersonalRagBundle {
  if (!v || typeof v !== "object") return false;
  const b = v as Record<string, unknown>;
  return (
    b.format === BUNDLE_FORMAT &&
    Array.isArray(b.documents) &&
    Array.isArray(b.conversations) &&
    Array.isArray(b.messages) &&
    Array.isArray(b.settings)
  );
}

export async function parseBundle(file: File): Promise<PersonalRagBundle> {
  let data: unknown;
  try {
    data = JSON.parse(await file.text());
  } catch {
    throw new Error(
      tr({
        fr: "Fichier illisible : ce n'est pas un export JSON valide.",
        en: "Unreadable file: this is not a valid JSON export.",
      }),
    );
  }
  if (!isBundle(data)) {
    throw new Error(
      tr({
        fr: "Ce fichier n'est pas un export Personal RAG (format non reconnu).",
        en: "This file is not a Personal RAG export (unknown format).",
      }),
    );
  }
  if (data.version > BUNDLE_VERSION) {
    throw new Error(
      tr({
        fr: `Cet export vient d'une version plus récente de l'application (v${data.version}). Mets à jour avant d'importer.`,
        en: `This export was made by a newer version of the app (v${data.version}). Update yours before importing.`,
      }),
    );
  }
  return data;
}

/**
 * Merge a bundle into the local IndexedDB. Non-destructive:
 *  - documents are deduplicated by content hash (existing docs are kept);
 *  - conversations already present (same id) are left untouched, and only
 *    messages of freshly-added conversations are written, so re-importing the
 *    same file twice is a no-op;
 *  - settings (config, font scale) overwrite local values — the importing user
 *    asked to carry their setup over. oidc.* keys are refused even if present.
 *
 * The caller should reload the app afterwards so in-memory state re-reads the
 * updated database.
 */
export async function importBundle(
  bundle: PersonalRagBundle,
): Promise<ImportSummary> {
  const summary: ImportSummary = {
    documentsAdded: 0,
    documentsSkipped: 0,
    conversationsAdded: 0,
    conversationsSkipped: 0,
    messagesAdded: 0,
    settingsApplied: 0,
  };

  const db = await getDB();
  const tx = db.transaction(
    ["documents", "conversations", "messages", "settings"],
    "readwrite",
  );
  const docStore = tx.objectStore("documents");
  const convStore = tx.objectStore("conversations");
  const msgStore = tx.objectStore("messages");
  const settingsStore = tx.objectStore("settings");

  for (const sdoc of bundle.documents) {
    const existing = await docStore.index("by-hash").get(sdoc.hash);
    if (existing) {
      summary.documentsSkipped++;
      continue;
    }
    const doc: StoredDocument = {
      ...sdoc,
      chunks: sdoc.chunks.map(deserializeChunk),
    };
    await docStore.put(doc);
    summary.documentsAdded++;
  }

  const addedConvIds = new Set<string>();
  for (const conv of bundle.conversations) {
    if (await convStore.get(conv.id)) {
      summary.conversationsSkipped++;
      continue;
    }
    await convStore.put(conv);
    addedConvIds.add(conv.id);
    summary.conversationsAdded++;
  }

  for (const msg of bundle.messages) {
    // Only attach messages to conversations we actually added this run, so a
    // re-import doesn't duplicate history under an existing conversation.
    if (!addedConvIds.has(msg.conversationId)) continue;
    await msgStore.put(msg);
    summary.messagesAdded++;
  }

  for (const s of bundle.settings) {
    if (SETTINGS_DENYLIST.some((p) => s.key.startsWith(p))) continue;
    await settingsStore.put(s);
    summary.settingsApplied++;
  }

  await tx.done;
  return summary;
}

function dateStamp(): string {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
